import { ThemeEvents } from '@theme/events';

/**
 * Shows how close the cart is to unlocking free shipping and updates live
 * whenever the cart changes (add to cart, quantity change, removal).
 */
class FreeShippingProgress extends HTMLElement {
  /** @type {number} */
  #total = 0;

  /** @type {boolean} */
  #inView = true;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {number | undefined} */
  #hideTimer;

  /** @type {AbortController | null} */
  #fetchController = null;

  connectedCallback() {
    this.#total = Number(this.dataset.total) || 0;

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    window.addEventListener('pageshow', this.#onPageShow);

    const card = this.querySelector('[ref="card"]');
    if (card && 'IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(([entry]) => {
        this.#inView = Boolean(entry?.isIntersecting);
        if (this.#inView) this.#hideFloating();
      });
      this.#observer.observe(card);
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
    this.#observer?.disconnect();
    this.#fetchController?.abort();
    clearTimeout(this.#hideTimer);
  }

  get threshold() {
    return Number(this.dataset.threshold) || 0;
  }

  #onCartUpdate = () => {
    this.#refresh(true);
  };

  /** Keep the bar correct when the page is restored from the back/forward cache. */
  #onPageShow = (/** @type {PageTransitionEvent} */ event) => {
    if (event.persisted) this.#refresh(false);
  };

  /**
   * Fetches the latest cart and re-renders the bar.
   * @param {boolean} animate - Whether to play the feedback animations.
   */
  async #refresh(animate) {
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        signal: this.#fetchController.signal,
      });
      if (!response.ok) return;

      const cart = await response.json();
      this.#render(cart.total_price, cart.item_count, cart.currency, animate);
    } catch (error) {
      if (/** @type {Error} */ (error).name !== 'AbortError') console.error(error);
    }
  }

  /**
   * @param {number} total - Cart total in the smallest currency unit.
   * @param {number} itemCount - Number of items in the cart.
   * @param {string} currency - ISO currency code.
   * @param {boolean} animate - Whether to play the feedback animations.
   */
  #render(total, itemCount, currency, animate) {
    const threshold = this.threshold;
    const wasUnlocked = this.#total >= threshold && this.#total > 0;
    const remaining = Math.max(threshold - total, 0);
    const percent = threshold > 0 ? Math.min(Math.round((total / threshold) * 100), 100) : 100;

    /** @type {'empty' | 'progress' | 'unlocked'} */
    let state;
    let message;

    if (itemCount === 0) {
      state = 'empty';
      message = (this.dataset.emptyText ?? '').replaceAll('[threshold]', this.#format(threshold, currency));
    } else if (remaining > 0) {
      state = 'progress';
      message = (this.dataset.progressText ?? '').replaceAll('[amount]', this.#format(remaining, currency));
    } else {
      state = 'unlocked';
      message = this.dataset.unlockedText ?? '';
    }

    this.#total = total;
    this.dataset.state = state;

    const messageEl = this.querySelector('[data-message]');
    const floatingMessageEl = this.querySelector('[data-floating-message]');
    if (messageEl) messageEl.innerHTML = message;
    if (floatingMessageEl) floatingMessageEl.innerHTML = message;

    for (const fill of this.querySelectorAll('[data-fill], [data-floating-fill]')) {
      /** @type {HTMLElement} */ (fill).style.setProperty('--fsp-percent', `${percent}%`);
    }

    this.querySelector('[data-track]')?.setAttribute('aria-valuenow', String(percent));

    const current = this.querySelector('[data-current]');
    if (current) current.textContent = this.#format(total, currency);

    if (!animate) return;

    this.#replayClass('is-bumped', 600);
    if (state === 'unlocked' && !wasUnlocked) this.#replayClass('is-celebrating', 1300);
    if (!this.#inView) this.#showFloating();
  }

  /**
   * @param {number} cents - Amount in the smallest currency unit.
   * @param {string} currency - ISO currency code.
   */
  #format(cents, currency) {
    const amount = cents / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency',
        currency: currency || this.dataset.currency || 'INR',
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return String(amount);
    }
  }

  /**
   * Re-triggers a CSS animation class.
   * @param {string} className
   * @param {number} duration
   */
  #replayClass(className, duration) {
    this.classList.remove(className);
    // Force a reflow so the animation restarts.
    void this.offsetWidth;
    this.classList.add(className);
    setTimeout(() => this.classList.remove(className), duration);
  }

  #showFloating() {
    if (!this.hasAttribute('data-floating')) return;
    const pill = this.querySelector('[data-floating-pill]');
    if (!pill) return;

    pill.classList.add('is-visible');
    clearTimeout(this.#hideTimer);
    this.#hideTimer = setTimeout(() => this.#hideFloating(), 4000);
  }

  #hideFloating() {
    clearTimeout(this.#hideTimer);
    this.querySelector('[data-floating-pill]')?.classList.remove('is-visible');
  }
}

if (!customElements.get('free-shipping-progress')) {
  customElements.define('free-shipping-progress', FreeShippingProgress);
}
